/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { History } from "lucide-react";
import type { ChangeEntry } from "../api/changes";
import { formatWhen } from "../lib/accounts";
import { Spinner } from "./ui/Spinner";

/**
 * An entity's recent changes, newest first. `labels` names fields in the product's words; an
 * `action.*` entry reads as the action itself, a field entry as before and after.
 */
export function ChangeList({
  changes,
  labels,
  emptyText = "No staff changes recorded yet.",
}: {
  changes: ChangeEntry[] | null;
  labels: Record<string, string>;
  emptyText?: string;
}) {
  if (changes === null) {
    return <Spinner size="sm" />;
  }
  if (changes.length === 0) {
    return <p className="text-sm text-text-tertiary">{emptyText}</p>;
  }
  return (
    <ul className="space-y-3">
      {changes.map((change) => (
        <li key={change.id} className="flex items-start gap-2.5 text-sm">
          <History size={14} className="mt-0.5 shrink-0 text-text-tertiary" />
          <div className="min-w-0">
            <p className="text-text-primary">{changeSummary(change, labels)}</p>
            <p className="text-xs text-text-tertiary">
              {formatWhen(change.changedAt)}
              {changedByLine(change)}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

export function changeSummary(change: ChangeEntry, labels: Record<string, string>): string {
  const label = labels[change.field];
  if (change.field.startsWith("action.")) {
    return label ?? change.field;
  }
  return `${label ?? change.field} changed from ${change.oldValue ?? "empty"} to ${
    change.newValue ?? "empty"
  }`;
}

function changedByLine(change: ChangeEntry): string {
  if (change.actorKind === "SYSTEM") {
    return " · by the system";
  }
  if (change.actorKind === "FAMILY") {
    return " · by the family";
  }
  return change.changedBy === null ? "" : ` · by staff ${change.changedBy}`;
}
